import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import UserService from 'services/user.api';
import BusinessService from 'services/business.api';
import { User, Business } from 'services/@types';

export default function useUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();

  const [user, setUser] = useState<User | null>(null);
  const [userBusiness, setUserBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const userData = await UserService.getUser(id);
      setUser(userData);

      if (userData.businessID) {
        try {
          const businessData = await BusinessService.getBusiness(
            userData.businessID,
          );
          setUserBusiness(businessData);
        } catch {
          // Business might not exist
        }
      }
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
      toast({
        title: t('toast.title.error'),
        description: 'Failed to load user details',
        status: 'error',
        duration: 3000,
      });
    }
    setLoading(false);
  }, [id, toast, t]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const updateUser = useCallback(
    async (updates: Partial<User>) => {
      if (!id) return;
      setSaving(true);
      try {
        const updatedUser = await UserService.updateUser(id, updates);
        setUser(updatedUser);
        toast({
          title: t('toast.title.success'),
          description: 'User updated successfully',
          status: 'success',
          duration: 3000,
        });
      } catch (error) {
        toast({
          title: t('toast.title.error'),
          description: 'Failed to update user',
          status: 'error',
          duration: 3000,
        });
      }
      setSaving(false);
    },
    [id, toast, t],
  );

  const toggleDelete = useCallback(async () => {
    if (!id || !user) return;
    await updateUser({ isDeleted: !user.isDeleted } as any);
  }, [id, user, updateUser]);

  const setOnboardingComplete = useCallback(async () => {
    if (!id) return;
    await updateUser({ onboardingStep: 'completed' } as any);
  }, [id, updateUser]);

  return {
    user,
    userBusiness,
    loading,
    saving,
    updateUser,
    toggleDelete,
    setOnboardingComplete,
    navigate,
    refetch: fetchUser,
  };
}
